import { useCallback } from 'react'
import { useSetAtom } from 'jotai'
import { useAtomCallback } from 'jotai/utils'
import { useQueryClient } from '@tanstack/react-query'
import { fetchConversation, statsAssistantKeys } from '../../queries/statsAssistant'
import {
  appendConversationMessage,
  patchConversationDetail,
  patchConversationSummary,
} from '../../queries/statsAssistant/statsAssistant.utils'
import { StatsAssistantEvent } from '../../../model/StatsAssistant'
import {
  patchTurn,
  statsAssistantProcessingUlidsAtom,
  statsAssistantTurnsAtom,
} from '../../../state/statsAssistant'

export const useStatsAssistantEvents = () => {
  const queryClient = useQueryClient()
  const setTurns = useSetAtom(statsAssistantTurnsAtom)

  const handleEvent = useCallback(
    ({ conversationUlid, ...event }: StatsAssistantEvent) => {
      switch (event.event) {
        case 'progress':
          setTurns((turns) =>
            patchTurn(turns, conversationUlid, {
              isProcessing: true,
              progressText: event.data.text,
            }),
          )
          break
        case 'message':
          appendConversationMessage(queryClient, conversationUlid, event.data)
          break
        case 'done':
          setTurns((turns) =>
            patchTurn(turns, conversationUlid, {
              isProcessing: false,
              progressText: null,
              error: event.data.error?.userMessage ?? null,
            }),
          )
          patchConversationDetail(queryClient, conversationUlid, () => ({
            isProcessing: false,
            title: event.data.title,
          }))
          patchConversationSummary(queryClient, conversationUlid, {
            isProcessing: false,
            title: event.data.title,
            updatedAt: new Date().toISOString(),
          })
          // Rattrape un message publié pendant une coupure réseau.
          void queryClient.invalidateQueries({
            queryKey: statsAssistantKeys.conversation(conversationUlid),
          })
          void queryClient.invalidateQueries({ queryKey: statsAssistantKeys.conversationsRoot })
          break
      }
    },
    [queryClient, setTurns],
  )

  /** Un done publié pendant une déconnexion est perdu : réaligne les tours encore en cours sur le back. */
  const resyncProcessingTurns = useAtomCallback(
    useCallback(
      (get, set) => {
        get(statsAssistantProcessingUlidsAtom).forEach((conversationUlid) => {
          void queryClient
            .fetchQuery({
              queryKey: statsAssistantKeys.conversation(conversationUlid),
              queryFn: () => fetchConversation(conversationUlid),
            })
            .then(({ isProcessing }) => {
              if (isProcessing) return
              set(statsAssistantTurnsAtom, (turns) =>
                patchTurn(turns, conversationUlid, { isProcessing: false, progressText: null }),
              )
            })
            .catch(() => undefined)
        })
      },
      [queryClient],
    ),
  )

  return { handleEvent, resyncProcessingTurns }
}
